import { useState } from 'react'
import { apercuContratRendu, envoyerContratSignature } from '../../api/candidatures'
import {
  COULEUR_STATUT_SIGNATURE,
  LIBELLE_METHODE,
  LIBELLE_STATUT_SIGNATURE,
  messageSignature,
  optionsEnvoiContrat,
  type MoyenEnvoiContrat,
  signatureCourante,
  signatureDocumensoEnCours,
} from '../../onboarding/contrats'
import type { ReponseContratRendu, SignatureContrat } from '../../onboarding/types'
import { lienWhatsApp } from '../../onboarding/whatsapp'
import { dateCourte, dateComplete } from '../Format'
import { Ligne } from '../Modale'
import { Erreur, useAction } from './Briques'
import { MarkdownRendu } from './MarkdownRendu'

/**
 * Section « Contrat » des deux fiches : la signature courante, et les deux
 * chemins de signature.
 *   (a) DANS L'APP : rien à faire ici : le candidat signe depuis son app dès
 *       qu'il a atteint l'étape.
 *   (b) PAR SIGNATURE ÉLECTRONIQUE (Documenso) : l'edge function
 *       `envoyer-contrat-signature` crée le document et renvoie un lien. L'équipe
 *       CHOISIT le canal : par e-mail (Documenso l'envoie) ou par WhatsApp (lien
 *       `wa.me` ouvert depuis la console, aucun e-mail). Un lien déjà créé peut
 *       repartir par l'un ou l'autre ; par e-mail seulement s'il a été préparé
 *       pour une vraie adresse. Si Documenso n'est pas encore installé, on
 *       l'explique (le message vient de la table `LISIBLE`).
 */
export function SectionContrat({
  kind,
  signerId,
  merchantId,
  nomSignataire,
  telephone,
  email,
  signatures,
  surChange,
}: {
  kind: 'livreur' | 'commercant'
  /** Profil du signataire (le livreur, ou le gérant de l'établissement). */
  signerId: string | null
  merchantId?: string
  nomSignataire: string
  telephone: string
  /** Adresse e-mail du signataire (vide si son compte a été créé avec un numéro seul). */
  email: string
  signatures: SignatureContrat[]
  surChange: () => void
}) {
  const courante = signatureCourante(signatures)
  const enCours = signatureDocumensoEnCours(signatures)
  const [choix, setChoix] = useState<MoyenEnvoiContrat | null>(null)
  const [lien, setLien] = useState<string | null>(null)
  const [telephoneRetour, setTelephoneRetour] = useState<string | null>(null)
  const [copie, setCopie] = useState(false)
  const [rendu, setRendu] = useState<ReponseContratRendu | null>(null)
  const { occupe, erreur, info, lancer } = useAction()
  const apercu = useAction()

  const lienAffiche = lien ?? enCours?.lien ?? null
  const dejaSigne = courante?.statut === 'signe'
  const numero = telephoneRetour ?? telephone
  const wa = lienAffiche ? lienWhatsApp(numero, messageSignature(nomSignataire, kind), lienAffiche, '223') : null
  const options = optionsEnvoiContrat({ email, telephone: numero }, kind)
  // Le choix de la personne tant qu'il reste possible ; sinon le canal par défaut (e-mail s'il existe, sinon WhatsApp).
  const moyen: MoyenEnvoiContrat | null = choix && options[choix] ? choix : options.defaut

  function ouvrirWhatsApp(url: string | null) {
    if (url) window.open(url, '_blank', 'noopener,noreferrer')
  }

  async function envoyer() {
    if (!signerId || !moyen) return
    // Lien déjà créé et envoi par WhatsApp : rien à demander au serveur, on ouvre WhatsApp avec ce lien.
    if (lienAffiche && moyen === 'whatsapp') {
      ouvrirWhatsApp(wa)
      return
    }
    await lancer(async () => {
      const r = await envoyerContratSignature({
        signerId,
        kind,
        ...(merchantId ? { merchantId } : {}),
        envoyerEmail: moyen === 'email',
      })
      setLien(r.lien_signature)
      setTelephoneRetour(r.telephone)
      surChange()
      if (r.email_impossible) {
        return "Ce contrat a été préparé sans adresse e-mail : il ne peut pas partir par e-mail. Envoyez-le par WhatsApp."
      }
      if (r.email_envoye) {
        return r.deja_envoye ? 'Lien de signature renvoyé par e-mail par Documenso.' : 'Lien créé et e-mail envoyé par Documenso.'
      }
      return "Lien créé. Cliquez sur « Envoyer par WhatsApp » pour le faire parvenir : aucun e-mail n'est parti."
    })
  }

  async function copier(texte: string) {
    try {
      await navigator.clipboard.writeText(texte)
      setCopie(true)
      window.setTimeout(() => setCopie(false), 2000)
    } catch {
      window.prompt('Copiez ce lien :', texte)
    }
  }

  return (
    <>
      {courante ? (
        <>
          <Ligne libelle="Méthode">{LIBELLE_METHODE[courante.methode]}</Ligne>
          <Ligne libelle="Statut">
            <span className={`badge badge-${COULEUR_STATUT_SIGNATURE[courante.statut]}`}>
              {LIBELLE_STATUT_SIGNATURE[courante.statut]}
            </span>
          </Ligne>
          <Ligne libelle="Version du contrat">v{courante.version}</Ligne>
          <Ligne libelle={courante.statut === 'signe' ? 'Signé le' : 'Envoyé le'}>
            {dateComplete(courante.accepteLe ?? courante.creeLe)}
          </Ligne>
          {kind === 'commercant' && (
            <Ligne libelle="Exclusivité">
              {courante.exclusiviteDebut
                ? `du ${dateCourte(courante.exclusiviteDebut)} au ${dateCourte(courante.exclusiviteFin)}`
                : 'non enregistrée (enregistrée à la signature)'}
            </Ligne>
          )}
          {courante.email && <Ligne libelle="Envoyé à">{courante.email}</Ligne>}
        </>
      ) : (
        <p className="dos-note">Aucune signature pour l'instant.</p>
      )}

      <div style={{ marginTop: 10 }}>
        <button
          className="btn btn-outline btn-sm"
          disabled={apercu.occupe || !signerId}
          onClick={() =>
            void apercu.lancer(async () => {
              setRendu(await apercuContratRendu(kind, merchantId ? { merchantId } : { signerId: signerId ?? '' }))
            })
          }
          title="Le texte tel qu'il sera signé : la désignation de la partie remplie d'après son identité"
        >
          {apercu.occupe ? 'Rendu…' : 'Voir le contrat rendu'}
        </button>
        {rendu && (
          <div style={{ marginTop: 6 }}>
            <p className="dos-note">
              Version {rendu.version}, empreinte SHA-256 du texte rendu : <code>{rendu.sha256.slice(0, 16)}…</code> (aperçu :
              rien n'est enregistré).
            </p>
            <div className="rendu-contrat"><MarkdownRendu source={rendu.texte_md} /></div>
            <button className="btn btn-outline btn-sm" style={{ marginTop: 6 }} onClick={() => setRendu(null)}>Masquer</button>
          </div>
        )}
        <Erreur texte={apercu.erreur} />
      </div>

      <p className="dos-note" style={{ marginTop: 10 }}>
        <b>Dans l'app :</b> rien à faire ici. {kind === 'livreur'
          ? 'Le livreur signe depuis son application dès que le quiz est réussi.'
          : "Le gérant signe depuis son application dès que le contrat lui a été envoyé (« Envoyer le contrat »)."}
      </p>

      <div style={{ marginTop: 10 }}>
        <p className="dos-note">
          <b>Par signature électronique :</b> crée un document Documenso et un lien de signature. Choisissez comment le faire parvenir.
        </p>
        <fieldset style={{ border: 0, margin: '6px 0', padding: 0 }}>
          <legend className="dos-note" style={{ padding: 0 }}>Envoyer le lien</legend>
          <label className="sous-liens" style={{ margin: '4px 0', fontSize: 12.5 }} title={options.raisonEmail ?? undefined}>
            <input type="radio" name={`canal-contrat-${kind}`} checked={moyen === 'email'} disabled={!options.email} onChange={() => setChoix('email')} />
            Par e-mail : Documenso envoie le lien{email ? ` à ${email}` : ''}
          </label>
          {options.raisonEmail && <div className="dos-note" style={{ margin: '0 0 4px 22px' }}>{options.raisonEmail}</div>}
          <label className="sous-liens" style={{ margin: '4px 0', fontSize: 12.5 }} title={options.raisonWhatsapp ?? undefined}>
            <input type="radio" name={`canal-contrat-${kind}`} checked={moyen === 'whatsapp'} disabled={!options.whatsapp} onChange={() => setChoix('whatsapp')} />
            Par WhatsApp : vous envoyez le lien{numero ? ` au ${numero}` : ''}, aucun e-mail ne part
          </label>
          {options.raisonWhatsapp && <div className="dos-note" style={{ margin: '0 0 4px 22px' }}>{options.raisonWhatsapp}</div>}
        </fieldset>
        <button
          className="btn btn-accent btn-sm"
          disabled={occupe || !signerId || dejaSigne || moyen === null}
          onClick={() => void envoyer()}
        >
          {occupe
            ? 'Envoi…'
            : lienAffiche
              ? moyen === 'whatsapp' ? 'Envoyer par WhatsApp' : 'Renvoyer par e-mail'
              : moyen === 'whatsapp' ? 'Préparer le lien et l\'envoyer par WhatsApp' : 'Envoyer le contrat par e-mail'}
        </button>
        {!signerId && (
          <p className="dos-note" style={{ marginTop: 4 }}>
            Impossible : aucun compte gérant n'est rattaché à cet établissement.
          </p>
        )}
        {options.blocage && signerId && !dejaSigne && <p className="dos-note" style={{ marginTop: 4 }}>{options.blocage}</p>}
        {dejaSigne && <p className="dos-note" style={{ marginTop: 4 }}>Le contrat est déjà signé.</p>}
        <Erreur texte={erreur} />
        {info && <div className="dos-ok">{info}</div>}
      </div>

      {lienAffiche && (
        <div style={{ marginTop: 10 }}>
          <p className="dos-note">{lien ? 'Lien de signature :' : 'Un lien de signature a déjà été envoyé :'}</p>
          <div className="dos-lien">
            <input readOnly value={lienAffiche} onFocus={(e) => e.currentTarget.select()} aria-label="Lien de signature" />
            <button className="btn btn-outline btn-sm" onClick={() => void copier(lienAffiche)}>
              {copie ? 'Copié' : 'Copier'}
            </button>
          </div>
          <div className="dos-champs">
            <button
              className="btn btn-green btn-sm"
              disabled={!wa}
              title={wa ? undefined : "Numéro de téléphone absent ou invalide (il faut l'indicatif, ex. +223…)"}
              onClick={() => ouvrirWhatsApp(wa)}
            >
              Envoyer par WhatsApp
            </button>
            {!wa && <span className="dos-note">Numéro WhatsApp invalide : copiez le lien.</span>}
          </div>
        </div>
      )}
    </>
  )
}
